import {Box, ButtonBase, Typography} from '@mui/material';
import React, {useId, useState} from 'react';
import {CORTILE, FONT} from './tokens';

export const StepHeading = ({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}) => (
  <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.25}}>
    <Typography
      component="h2"
      sx={{
        m: 0,
        fontFamily: FONT,
        fontSize: 32,
        fontWeight: 600,
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        color: CORTILE.slate,
      }}
    >
      {title}
    </Typography>
    {children && (
      <Typography
        sx={{
          m: 0,
          fontFamily: FONT,
          fontSize: 15,
          lineHeight: 1.5,
          color: CORTILE.muted,
          textWrap: 'pretty',
        }}
      >
        {children}
      </Typography>
    )}
  </Box>
);

export const Strong = ({children}: {children: React.ReactNode}) => (
  <Box component="strong" sx={{color: CORTILE.slate, fontWeight: 600}}>
    {children}
  </Box>
);

type CortileInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  invalid: boolean;
  trailingSpace?: boolean;
};

const CortileInput = React.forwardRef<HTMLInputElement, CortileInputProps>(
  ({invalid, trailingSpace, ...inputProps}, ref) => (
    <Box
      component="input"
      ref={ref}
      aria-invalid={invalid}
      sx={{
        height: 46,
        width: '100%',
        flex: '1 1 auto',
        minWidth: 0,
        boxSizing: 'border-box',
        border: `1px solid ${invalid ? CORTILE.error : CORTILE.border}`,
        borderRadius: '8px',
        pl: '14px',
        pr: trailingSpace ? '64px' : '14px',
        fontFamily: FONT,
        fontSize: 15,
        color: CORTILE.slate,
        bgcolor: '#FFFFFF',
        outline: 'none',
        '&::placeholder': {color: CORTILE.placeholder},
        '&:focus': {
          borderColor: CORTILE.terracotta,
          boxShadow: CORTILE.focusRing,
        },
      }}
      {...inputProps}
    />
  ),
);
CortileInput.displayName = 'CortileInput';

type FieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
};

export const TextField = React.forwardRef<HTMLInputElement, FieldProps>(
  ({label, error, ...inputProps}, ref) => {
    const errorId = useId();
    return (
      <Box
        component="label"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          fontFamily: FONT,
          fontSize: 14,
          fontWeight: 600,
          color: CORTILE.slate,
        }}
      >
        {label}
        <CortileInput
          ref={ref}
          invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          {...inputProps}
        />
        {error && <FieldError id={errorId}>{error}</FieldError>}
      </Box>
    );
  },
);
TextField.displayName = 'TextField';

export const PasswordField = React.forwardRef<HTMLInputElement, FieldProps>(
  ({label, error, ...inputProps}, ref) => {
    const [visible, setVisible] = useState(false);
    const errorId = useId();
    return (
      <Box
        component="label"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          fontFamily: FONT,
          fontSize: 14,
          fontWeight: 600,
          color: CORTILE.slate,
        }}
      >
        {label}
        <Box component="span" sx={{position: 'relative', display: 'flex'}}>
          <CortileInput
            ref={ref}
            type={visible ? 'text' : 'password'}
            invalid={!!error}
            trailingSpace
            aria-describedby={error ? errorId : undefined}
            {...inputProps}
          />
          <ButtonBase
            type="button"
            onClick={() => setVisible(v => !v)}
            data-attr="cortile-login-password-visibility-toggle"
            aria-label={visible ? 'Hide password' : 'Show password'}
            sx={{
              position: 'absolute',
              right: 6,
              top: 7,
              height: 32,
              px: 1.25,
              borderRadius: '6px',
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: 13,
              color: CORTILE.muted,
              '&:hover': {bgcolor: CORTILE.hoverFill, color: CORTILE.slate},
            }}
          >
            {visible ? 'Hide' : 'Show'}
          </ButtonBase>
        </Box>
        {error && <FieldError id={errorId}>{error}</FieldError>}
      </Box>
    );
  },
);
PasswordField.displayName = 'PasswordField';

export const FieldError = ({
  children,
  id,
}: {
  children: React.ReactNode;
  id?: string;
}) => (
  <Box
    component="span"
    id={id}
    role="alert"
    sx={{fontFamily: FONT, fontSize: 13, fontWeight: 500, color: CORTILE.error}}
  >
    {children}
  </Box>
);

export const PrimaryButton = ({
  children,
  disabled,
  loading,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {loading?: boolean}) => (
  <ButtonBase
    disabled={disabled || loading}
    sx={{
      height: 48,
      borderRadius: '8px',
      bgcolor: disabled ? CORTILE.disabled : CORTILE.slate,
      color: CORTILE.cream,
      fontFamily: FONT,
      fontWeight: 600,
      fontSize: 16,
      mt: 0.5,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.7 : 1,
      '&:hover': {bgcolor: disabled ? CORTILE.disabled : CORTILE.slateHover},
    }}
    {...props}
  >
    {children}
  </ButtonBase>
);

export const SecondaryButton = (
  props: React.ButtonHTMLAttributes<HTMLButtonElement>,
) => (
  <ButtonBase
    type="button"
    sx={{
      height: 40,
      px: 2,
      border: `1px solid ${CORTILE.border}`,
      borderRadius: '8px',
      bgcolor: '#FFFFFF',
      fontFamily: FONT,
      fontWeight: 600,
      fontSize: 14,
      color: CORTILE.slate,
      '&:hover': {borderColor: CORTILE.slate},
      '&.Mui-disabled': {
        color: CORTILE.muted,
        borderColor: CORTILE.divider,
        cursor: 'not-allowed',
      },
    }}
    {...props}
  />
);

export const TextButton = ({
  children,
  back,
  align = 'center',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  back?: boolean;
  align?: 'center' | 'flex-start';
}) => (
  <ButtonBase
    type="button"
    sx={{
      alignSelf: align,
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      p: 0.5,
      fontFamily: FONT,
      fontSize: 14,
      fontWeight: 500,
      color: CORTILE.slate,
      textDecoration: back ? 'none' : 'underline',
      textUnderlineOffset: 3,
      '&:hover': {color: CORTILE.terracottaDark},
    }}
    {...props}
  >
    {back && (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
    )}
    {children}
  </ButtonBase>
);

export const Form = ({
  children,
  ...props
}: React.FormHTMLAttributes<HTMLFormElement>) => (
  <Box
    component="form"
    noValidate
    sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}
    {...props}
  >
    {children}
  </Box>
);
